import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, MessageSquare, FileText, Calendar, Users, CheckSquare,
  Building2, Package, Boxes, Settings, BarChart3, Tags, Wallet, Briefcase,
  UserCog, UserCircle, CreditCard, MapPin, CalendarDays, Banknote, Globe,
  Database, BedDouble, Activity, ReceiptIndianRupee, Monitor, Search, ArrowRight,
  Plus, TrendingUp, Clock,
} from "lucide-react";

interface CommandItem {
  id: string;
  label: string;
  description?: string;
  href?: string;
  icon: any;
  group: string;
  keywords?: string[];
}

const allCommands: CommandItem[] = [
  // Operations
  { id: "dashboard",  label: "Dashboard",  description: "Overview & KPIs",                href: "/dashboard",  icon: LayoutDashboard, group: "Operations", keywords: ["home", "overview"] },
  { id: "enquiries",  label: "Enquiries",  description: "Manage incoming enquiries",       href: "/enquiries",  icon: MessageSquare,   group: "Operations", keywords: ["leads", "inquiry"] },
  { id: "quotations", label: "Quotations", description: "Create & manage quotations",       href: "/quotations", icon: FileText,         group: "Operations", keywords: ["quote", "proposal"] },
  { id: "events",     label: "Events",     description: "Full event lifecycle management",  href: "/events",     icon: Calendar,        group: "Operations", keywords: ["booking", "wedding"] },
  { id: "clients",    label: "Clients",    description: "Client profiles & ledger",         href: "/clients",    icon: UserCircle,      group: "Operations", keywords: ["customer", "contact"] },
  { id: "calendar",   label: "Calendar",   description: "Schedule & availability view",     href: "/calendar",   icon: CalendarDays,    group: "Operations", keywords: ["schedule", "availability"] },
  // Finance
  { id: "expenses",        label: "Expenses",        description: "Track & manage expenses",    href: "/expenses",        icon: Wallet,        group: "Finance", keywords: ["cost", "spend"] },
  { id: "vendor-payments", label: "Vendor Payments", description: "Payments to vendors",        href: "/vendor-payments", icon: CreditCard,    group: "Finance", keywords: ["pay", "supplier payment"] },
  // Planning
  { id: "tasks",   label: "Tasks",   description: "Event task assignments",  href: "/tasks",   icon: CheckSquare, group: "Planning", keywords: ["todo", "assign"] },
  { id: "reports", label: "Reports", description: "Analytics & insights",    href: "/reports", icon: BarChart3,   group: "Planning", keywords: ["analytics", "stats"] },
  // Catalog
  { id: "packages", label: "Packages",        description: "Event package bundles",  href: "/packages", icon: Boxes,   group: "Catalog", keywords: ["bundle", "pricing"] },
  { id: "services", label: "Service Catalog", description: "Services & pricing",     href: "/services", icon: Package, group: "Catalog", keywords: ["catalog", "pricing"] },
  // Venue & Rooms
  { id: "venues",        label: "Venues",        description: "Venue management",           href: "/venues",        icon: MapPin,   group: "Venue & Rooms" },
  { id: "rooms",         label: "Rooms",         description: "Room inventory",             href: "/rooms",         icon: Building2, group: "Venue & Rooms" },
  { id: "room-bookings", label: "Room Bookings", description: "Room reservation management",href: "/room-bookings", icon: BedDouble, group: "Venue & Rooms" },
  // People
  { id: "staff",   label: "Staff",   description: "User accounts & roles",       href: "/staff",   icon: UserCog, group: "People", keywords: ["users", "team", "employees"] },
  { id: "vendors", label: "Vendors", description: "External vendor management",   href: "/vendors", icon: Users,   group: "People", keywords: ["supplier", "contractor"] },
  // Admin
  { id: "settings",           label: "Settings",           description: "Banquet hall configuration",   href: "/settings",           icon: Settings,          group: "Admin", keywords: ["config"] },
  { id: "event-types",        label: "Event Types",        description: "Manage event categories",      href: "/event-types",        icon: CalendarDays,      group: "Admin" },
  { id: "vendor-types",       label: "Vendor Categories",  description: "Vendor type management",       href: "/vendor-types",       icon: Briefcase,         group: "Admin" },
  { id: "payment-methods",    label: "Payment Methods",    description: "Configure payment options",    href: "/payment-methods",    icon: Banknote,          group: "Admin" },
  { id: "expense-categories", label: "Expense Categories", description: "Expense category setup",       href: "/expense-categories", icon: Tags,              group: "Admin" },
  { id: "frontend-settings",  label: "Site Settings",      description: "Website content management",   href: "/frontend-settings",  icon: Monitor,           group: "Admin" },
  { id: "activity-logs",      label: "Activity Logs",      description: "System activity history",      href: "/activity-logs",      icon: Activity,          group: "Admin" },
  { id: "payment-logs",       label: "Payment Logs",       description: "Payment transaction history",  href: "/payment-logs",       icon: ReceiptIndianRupee,group: "Admin" },
];

const RECENT_KEY = "cmd_recent_pages";
const MAX_RECENT = 5;

function getRecent(): string[] {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); } catch { return []; }
}
function addRecent(id: string) {
  const list = [id, ...getRecent().filter(x => x !== id)].slice(0, MAX_RECENT);
  localStorage.setItem(RECENT_KEY, JSON.stringify(list));
}

interface Props { isOpen: boolean; onClose: () => void; }

export default function CommandPalette({ isOpen, onClose }: Props) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const filtered = useCallback(() => {
    if (!query.trim()) {
      const recentIds = getRecent();
      const recentItems = recentIds
        .map(id => allCommands.find(c => c.id === id))
        .filter(Boolean) as CommandItem[];
      return recentItems.length > 0
        ? [{ group: "Recent", items: recentItems }]
        : [{ group: "Pages", items: allCommands.filter(c => c.group === "Pages").slice(0, 6) }];
    }
    const q = query.toLowerCase();
    const matched = allCommands.filter(c =>
      c.label.toLowerCase().includes(q) ||
      (c.description || "").toLowerCase().includes(q) ||
      (c.keywords || []).some(k => k.toLowerCase().includes(q))
    );
    const groups: Record<string, CommandItem[]> = {};
    matched.forEach(item => {
      if (!groups[item.group]) groups[item.group] = [];
      groups[item.group].push(item);
    });
    return Object.entries(groups).map(([group, items]) => ({ group, items }));
  }, [query]);

  const results = filtered();
  const flatItems = results.flatMap(g => g.items);

  useEffect(() => { setSelectedIndex(0); }, [query]);

  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const handleSelect = (item: CommandItem) => {
    if (item.href) {
      addRecent(item.id);
      navigate(item.href);
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, flatItems.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (flatItems[selectedIndex]) handleSelect(flatItems[selectedIndex]);
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-index="${selectedIndex}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  if (!isOpen) return null;

  let flatIndex = 0;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh] px-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-stone-900/50 backdrop-blur-sm" />

      {/* Panel */}
      <div
        className="relative w-full max-w-xl bg-white rounded-2xl shadow-2xl border border-stone-200 overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Search Input */}
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-stone-100">
          <Search className="w-4 h-4 text-stone-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search pages, actions…"
            className="flex-1 text-sm text-stone-800 placeholder-stone-400 outline-none bg-transparent"
          />
          <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 rounded text-xs font-mono text-stone-400 border border-stone-200 bg-stone-50">
            esc
          </kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-80 overflow-y-auto py-2">
          {results.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-stone-400">
              No results for "{query}"
            </div>
          ) : (
            results.map(({ group, items }) => (
              <div key={group}>
                <p className="px-4 pt-2 pb-1 text-xs font-semibold text-stone-400 uppercase tracking-wider flex items-center gap-1.5">
                  {group === "Recent" && <Clock className="w-3 h-3" />}
                  {group}
                </p>
                {items.map(item => {
                  const Icon = item.icon;
                  const idx = flatIndex++;
                  const isSelected = idx === selectedIndex;
                  return (
                    <button
                      key={item.id}
                      data-index={idx}
                      onClick={() => handleSelect(item)}
                      onMouseEnter={() => setSelectedIndex(idx)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                        isSelected ? "bg-amber-50" : "hover:bg-stone-50"
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        isSelected ? "bg-amber-100 text-amber-700" : "bg-stone-100 text-stone-500"
                      }`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${isSelected ? "text-amber-800" : "text-stone-700"}`}>
                          {item.label}
                        </p>
                        {item.description && (
                          <p className="text-xs text-stone-400 truncate">{item.description}</p>
                        )}
                      </div>
                      {isSelected && <ArrowRight className="w-4 h-4 text-amber-400 flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-stone-100 px-4 py-2 flex items-center gap-4 text-xs text-stone-400">
          <span className="flex items-center gap-1"><kbd className="font-mono border border-stone-200 rounded px-1 bg-stone-50">↑↓</kbd> navigate</span>
          <span className="flex items-center gap-1"><kbd className="font-mono border border-stone-200 rounded px-1 bg-stone-50">↵</kbd> open</span>
          <span className="flex items-center gap-1"><kbd className="font-mono border border-stone-200 rounded px-1 bg-stone-50">esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
}
